/**
 * Pipeline page component for deals.
 *
 * Composes the pipeline header/toolbar actions with PipelineBoard.
 *
 * This is a standalone page component that handles data fetching, pipeline
 * selection, search/filter, sort, and rendering.
 */

import { AlignJustify, ArrowDown, Settings01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SortDescriptor } from 'react-aria-components';
import { useParams, useSearchParams } from 'react-router-dom';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { useCanWrite, usePermissions } from '@/hooks/usePermissions';
import { useTitle } from '@/hooks/useTitle';
import { Button } from '@/lib/base/buttons/button';
import { FormProvider } from '@/lib/base/forms/form-context';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import type { HeaderAction } from '@/lib/base/page-layout/page-layout';
import { ToolbarSection } from '@/lib/base/page-layout/toolbar';
import SchemaFilterBuilder, {
  hasIncompleteFilterConditions,
} from '@/lib/crm/components/SchemaFilterBuilder';
import { buildGenericCreateConfig, useCreateSlideout } from '@/lib/crm/create';
import type { CrmCreateClickHandler } from '@/lib/crm/create/createOverrides';
import type { FilterCondition } from '@/lib/crm/filters/types';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import { useListDefaultView } from '@/lib/crm/hooks/useListDefaultView';
import { getPipelineAssociationTypeName } from '@/lib/crm/list/utils/pipelineScopeFilter';
import { getEntityKinds } from '@/lib/crm/utils/associationMatching';
import { resolveOwnerAssociationName } from '@/lib/crm/utils/ownerAssociation';
import { isTransientError } from '@/lib/errors';
import type {
  AssociationDetail,
  AssociationFilter,
  CrmFilter,
  CrmObject,
  CrmObjectFilters,
  FilterGroup,
} from '@/types/crm';
import { isAssociationFilter } from '@/types/crm';
import type { SavedFilterView } from '@/types/savedFilterView';
import { cx } from '@/utils/cx';
import {
  convertConditionGroupsToFilterGroups,
  filterGroupsToConditionGroups,
} from '@/utils/filterUtils';

import { isPersonType } from '../../core/entityTypes';
import type { EntityTypeName, StageMetricsData } from '../../core/types';
import { useActiveListView } from '../../hooks/useActiveListView';
import { useAssociationMatchers } from '../../hooks/useAssociationMatchers';
import { useAssociationTypes } from '../../hooks/useAssociationTypes';
import { useFieldTypes } from '../../hooks/useFieldTypes';
import type { PipelineStageSchema } from '../../hooks/usePipelines';
import { usePipelines } from '../../hooks/usePipelines';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import { usePropertySchema } from '../../hooks/usePropertySchema';
import type { SavedViewSurface } from '../../hooks/useSavedViews';
import { useSavedViews } from '../../hooks/useSavedViews';
import { useSlideoutStack } from '../../hooks/useSlideoutStack';
import {
  type PipelineStageInput,
  useUpdatePipelineStages,
} from '../../hooks/useUpdatePipelineStages';
import { CrmListLayout } from '../../list/components/crm-list-layout';
import { SaveCurrentViewModal } from '../../list/components/SaveCurrentViewModal';
import { SavedViewTabs } from '../../list/components/SavedViewTabs';
import { useListUrlSync } from '../../list/hooks/useListUrlSync';
import {
  decodeFiltersParam,
  FILTERS_PARAM,
  MODE_PARAM,
  readInitialListUrlState,
} from '../../list/urlListParams';
import type { PipelineStage } from '../../pipeline/types';
import { formatPipelineLabel } from '../../pipeline/utils/formatPipelineLabel';
import { useCrmObjectList } from '../../store/hooks';
import { useDealStageMove } from '../hooks/useDealStageMove';
import { usePipelineCardConfig } from '../hooks/usePipelineCardConfig';
import { usePipelineFooterConfig } from '../hooks/usePipelineFooterConfig';
import type { AddStageSubmitData } from './AddStageModal';
import { ConfigurableCardContent } from './ConfigurableCardContent';
import { PipelineBoard } from './PipelineBoard';

// ---------------------------------------------------------------------------
// Sort options
// ---------------------------------------------------------------------------

// `label` is title case for standalone menu use. `sentenceLabel` is the same
// name written for mid-sentence interpolation (e.g. the board's "Resorting by
// …" chip), which the tone guide requires to be sentence case. Stored
// explicitly rather than lower-cased at the call site so a future option
// containing a proper noun doesn't silently render wrong.
const ALL_SORT_OPTIONS = [
  {
    value: 'ahoy_created_at',
    label: 'Date Created',
    sentenceLabel: 'date created',
  },
  {
    value: 'ahoy_updated_at',
    label: 'Last Updated',
    sentenceLabel: 'last updated',
  },
  {
    value: 'ahoy_last_activity_at',
    label: 'Last Activity',
    sentenceLabel: 'last activity',
  },
  { value: 'ahoy_amount', label: 'Amount', sentenceLabel: 'amount' },
  {
    value: 'ahoy_close_date',
    label: 'Close Date',
    sentenceLabel: 'close date',
  },
] as const;

// The filter builder renders into groups; an empty filter state is a single
// empty group.
// The pipeline board's default sort — also the clean-URL `?sort=` baseline
// for useListUrlSync (the pipeline never applies a saved view's stored
// view_state, so the default is the baseline on saved views too).
const PIPELINE_DEFAULT_SORT_BY = 'ahoy_created_at';
const PIPELINE_DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';
const PIPELINE_DEFAULT_SORT_DESCRIPTOR = {
  column: PIPELINE_DEFAULT_SORT_BY,
  direction: 'descending' as const,
};
// The pipeline has no visible default filters — the clean-URL baseline is empty.
const PIPELINE_EMPTY_DEFAULT_FILTER_GROUPS: FilterGroup[] = [];

const EMPTY_BUILDER_GROUPS: FilterConditionGroup[] = [
  { id: 'group-0', conditions: [] },
];

/**
 * Properties that exist on every entity type (always included).
 *
 * `ahoy_display_name` is the universal name property — provided by every
 * entity_kind's base properties (default, pipeline, stage). `ahoy_name`
 * is NOT universal — it's a deal-specific user-input field on `ahoy_deal`
 * (and a base property on pipeline/stage kinds), but custom default-kind
 * entities don't have it. Code that needs "the name" should prefer
 * `ahoy_display_name`; query `ahoy_name` only when the entity's schema
 * declares it.
 */
const UNIVERSAL_PROPERTIES = new Set([
  'ahoy_created_at',
  'ahoy_updated_at',
  'ahoy_display_name',
]);

// ---------------------------------------------------------------------------
// PipelineStageSchema → PipelineStageInput
//
// All six stage-mutating handlers (rename, add, move, reorder, recolor,
// delete) build the full `PipelineStageInput[]` payload from the current
// stages. Since the PUT contract requires the complete array on every call,
// the shared mapping centralizes the field translation in one place. Each
// handler then mutates only the field it cares about.
// ---------------------------------------------------------------------------

function toStageInput(s: PipelineStageSchema): PipelineStageInput {
  return {
    ...(s.entity_id ? { entity_id: s.entity_id } : {}),
    name: s.label,
    probability: s.properties.probability,
    order: s.properties.order,
    color: s.properties.color ?? null,
  };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PipelinePageProps {
  entityType: EntityTypeName;
  /** Entity display name */
  title: string;
  pluralTitle: string;
  /** Properties for create form */
  createProperties: string[];
  /** Association type names for create form */
  createAssociations: string[];
  /** Stage association type name (e.g., 'ahoy_deal_to_deal_pipeline_stage') */
  stageAssociationTypeName: string;
  /** Callback for creating an object */
  onCreateObject?: (
    orgId: number,
    data: Record<string, unknown>
  ) => Promise<void>;
  /**
   * Override what happens when the Add button is clicked. When provided, this
   * callback is invoked instead of the built-in create slideout — use for
   * entity types with custom create flows (e.g., NoteCreateModal).
   *
   * The optional `defaults` arg lets us pass pre-fill values — notably the
   * `stageEntityId` of the column when the user clicks the per-stage `+`
   * affordance, so the override modal can auto-select that stage.
   */
  onCreateClick?: CrmCreateClickHandler;
  /** Label for the "All {entities}" header action that links to the table view */
  allItemsLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PipelinePage({
  entityType,
  title,
  pluralTitle,
  createProperties,
  createAssociations,
  stageAssociationTypeName,
  onCreateObject,
  onCreateClick,
  allItemsLabel,
}: PipelinePageProps) {
  useTitle(pluralTitle);
  const { pipelineId: pipelineIdFromRoute } = useParams<{
    pipelineId?: string;
  }>();
  const [searchParams] = useSearchParams();
  // `topId` is the entity currently shown in the slideout — used to ring its
  // card on the board so it's obvious which item the panel belongs to. It is
  // undefined when the stack is empty OR the top entry isn't a CRM entity
  // (e.g. a proposal), which is the behaviour we want: nothing to point at.
  //
  // Safe to compare against `getItemId` (which returns `item.id`): `entity_id`
  // "returns the same value as id" — see types/crm.ts:97.
  const { openSlideout, topId: slideoutTopId } = useSlideoutStack();
  const { open: openCreateSlideout } = useCreateSlideout();
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const orgId = currentOrg?.id;

  const {
    defaultViewRef,
    isDefaultResolving,
    setDefaultView,
    clearDefaultView,
  } = useListDefaultView(currentOrg?.id, entityType);

  // Mount-time snapshot of the URL-synced list params (?filters=/?sort=/
  // ?search=), frozen so re-renders never re-seed; kept current for
  // imperative readers via searchParamsRef.
  const [initialUrlState] = useState(() =>
    readInitialListUrlState(searchParams)
  );
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const getUrlFilterOverrides = useCallback(
    () => decodeFiltersParam(searchParamsRef.current.get(FILTERS_PARAM)),
    []
  );

  // --- Sort state (seeded from the URL when present) ---
  const [sortBy, setSortBy] = useState<string>(() =>
    initialUrlState.sortDescriptor
      ? String(initialUrlState.sortDescriptor.column)
      : PIPELINE_DEFAULT_SORT_BY
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    if (!initialUrlState.sortDescriptor) {
      return PIPELINE_DEFAULT_SORT_ORDER;
    }
    return initialUrlState.sortDescriptor.direction === 'ascending'
      ? 'asc'
      : 'desc';
  });

  // --- Search/filter state (seeded from the URL when present) ---
  const [searchTerm, setSearchTerm] = useState(
    () => initialUrlState.search ?? ''
  );
  const [filterConditionGroups, setFilterConditionGroups] = useState<
    FilterConditionGroup[]
  >(() =>
    initialUrlState.filterGroups?.length
      ? filterGroupsToConditionGroups(initialUrlState.filterGroups)
      : []
  );
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);
  const [filtersAreValid, setFiltersAreValid] = useState(true);

  // Flat read across all groups — for the chip count/tooltip and the validity
  // form only, never for building the backend query.
  const filterConditions = useMemo<FilterCondition[]>(
    () => filterConditionGroups.flatMap(g => g.conditions),
    [filterConditionGroups]
  );

  const filterFieldConfig = useMemo(
    () => ({
      filters: {
        validate: (val: unknown) => {
          const { conditions, valid } = val as {
            conditions: FilterCondition[];
            valid: boolean;
          };
          if (!valid) {
            return 'Some filter values are invalid';
          }
          if (hasIncompleteFilterConditions(conditions)) {
            return 'Some filter conditions are incomplete';
          }
          return undefined;
        },
      },
    }),
    []
  );

  const filterFormValues = useMemo(
    () => ({
      filters: { conditions: filterConditions, valid: filtersAreValid },
    }),
    [filterConditions, filtersAreValid]
  );

  const builderGroups = useMemo<FilterConditionGroup[]>(
    () =>
      filterConditionGroups.length > 0
        ? filterConditionGroups
        : EMPTY_BUILDER_GROUPS,
    [filterConditionGroups]
  );

  const handleGroupsChange = useCallback(
    (next: FilterConditionGroup[]) => setFilterConditionGroups(next),
    []
  );

  // Hoisted up so downstream useMemos (cardFields, queryProperties) can
  // branch on entity kind without forward-referencing.
  const isDealType = entityType === 'ahoy_deal';

  // --- Property schemas ---
  const { schema: propertySchemas, loading: schemaLoading } = usePropertySchema(
    orgId ?? 0,
    entityType
  );
  // --- Sort options filtered by entity schema ---
  const sortOptions = useMemo(
    () =>
      ALL_SORT_OPTIONS.filter(
        opt =>
          UNIVERSAL_PROPERTIES.has(opt.value) ||
          propertySchemas[opt.value] !== undefined
      ),
    [propertySchemas]
  );

  // Sentence-case name of the active sort, for the board's "Resorting by …"
  // chip. Null when the active sort key isn't a known option.
  const sortLabel = useMemo(
    () =>
      ALL_SORT_OPTIONS.find(opt => opt.value === sortBy)?.sentenceLabel ?? null,
    [sortBy]
  );

  // --- Association types ---
  const { associationTypes, associationTypeMap, associationLabelsByName } =
    useAssociationTypes(orgId);
  const { entityKindMap } = useAssociationMatchers(orgId);

  // Kind-aware owner association name (see docs/claude comment in
  // ownerAssociation.ts) — used for both the include_associations fetch
  // projection and the card footer's owner lookup.
  const ownerAssocName = useMemo(
    () =>
      resolveOwnerAssociationName(
        associationTypes,
        entityType,
        getEntityKinds(entityType, entityKindMap)
      ),
    [associationTypes, entityType, entityKindMap]
  );

  // --- Field types (aggregation eligibility for the stage footer) ---
  const { data: fieldTypes } = useFieldTypes();
  const fieldTypeAggregations = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const ft of fieldTypes ?? []) {
      map[ft.field_type_name] = ft.aggregations;
    }
    return map;
  }, [fieldTypes]);

  /** Maps association_type_name → display label for card label rendering. */
  const associationLabels = useMemo(
    () => new Map(Object.entries(associationLabelsByName)),
    [associationLabelsByName]
  );

  // --- Permissions ---
  const { isSchemaAdmin, hasPermission } = usePermissions();
  // Canonical residual-affordance write gate: viewers (no 'ahoy_default'
  // group) see no write affordances at all — see crm-object-list-page.tsx.
  const canWrite = useCanWrite();

  // --- Stages and pipelines ---
  const {
    stagesByEntityId,
    isLoading: stagesLoading,
    isSettingUp: stagesSettingUp,
    refetch: refetchStages,
    ratesUnavailable,
  } = usePipelineStages(orgId, entityType);
  const {
    pipelines,
    isLoading: pipelinesLoading,
    error: pipelinesError,
  } = usePipelines(orgId, entityType);

  // Pipeline selection
  const defaultPipelineId =
    (pipelines.find(p => p.is_default) || pipelines[0])?.entity_id ?? '';
  const [localSelectedPipelineId, setLocalSelectedPipelineId] =
    useState<string>(defaultPipelineId);
  const selectedPipelineId = pipelineIdFromRoute || localSelectedPipelineId;

  const userSelectedPipelineRef = useRef(!!pipelineIdFromRoute);
  const prevOrgIdRef = useRef(orgId);

  // Sync pipeline selection
  useEffect(() => {
    if (pipelineIdFromRoute) {
      userSelectedPipelineRef.current = true;
      return;
    }

    const pipelineFromUrl = searchParams.get('pipeline');
    if (pipelineFromUrl && pipelines.length > 0) {
      const matched = pipelines.find(p => p.pipeline_name === pipelineFromUrl);
      if (matched?.entity_id && matched.entity_id !== localSelectedPipelineId) {
        userSelectedPipelineRef.current = true;
        setLocalSelectedPipelineId(matched.entity_id);
        return;
      }
    }

    const orgChanged = prevOrgIdRef.current !== orgId;
    if (orgChanged) {
      userSelectedPipelineRef.current = false;
      prevOrgIdRef.current = orgId;
    }

    if (
      pipelines.length > 0 &&
      localSelectedPipelineId &&
      !pipelines.some(p => p.entity_id === localSelectedPipelineId)
    ) {
      userSelectedPipelineRef.current = false;
    }

    if (userSelectedPipelineRef.current) {
      return;
    }

    const defaultPipeline = pipelines.find(p => p.is_default) || pipelines[0];
    if (defaultPipeline?.entity_id) {
      setLocalSelectedPipelineId(defaultPipeline.entity_id);
    }
  }, [
    orgId,
    pipelines,
    localSelectedPipelineId,
    pipelineIdFromRoute,
    searchParams,
  ]);

  const selectedPipeline = useMemo(() => {
    const matched = pipelines.find(p => p.entity_id === selectedPipelineId);
    if (matched) {
      return matched;
    }
    // Stage-only taxonomies (e.g. contact lifecycle) expose a single pipeline
    // with no entity_id, so it can't be matched by id. Fall back to it when
    // there's exactly one pipeline. Multi-pipeline (deal) selection is
    // unaffected: those pipelines always have real entity_ids that match here.
    return pipelines.length === 1 ? pipelines[0] : undefined;
  }, [pipelines, selectedPipelineId]);

  // --- Card config (entity-backed when a pipeline entity is selected) ---
  const {
    config: cardConfig,
    propertyNames: cardPropertyNames,
    associationNames: cardAssociationNames,
  } = usePipelineCardConfig(
    orgId,
    entityType,
    propertySchemas,
    associationTypeMap,
    selectedPipeline?.entity_id,
    selectedPipeline?.entity_type_name,
    selectedPipeline?.properties?.ahoy_card_config
  );

  // --- Stage footer config (entity-backed when a pipeline entity is selected) ---
  // `isDealType` (entityType === 'ahoy_deal') is the object/base entity type,
  // so it also distinguishes a deal pipeline for the footer default.
  const { config: footerConfig, propertyNames: footerPropertyNames } =
    usePipelineFooterConfig(
      orgId,
      entityType,
      isDealType,
      propertySchemas,
      fieldTypeAggregations,
      selectedPipeline?.entity_id,
      selectedPipeline?.entity_type_name,
      selectedPipeline?.properties?.ahoy_stage_footer_config
    );

  // ── Stage editing (rename / add / move / recolor / delete) ─────────────
  // Permission gates match the existing Settings → Pipelines flow:
  //   edit   → hasPermission(pipeline.entity_type_name, 'ahoy_property_updated')
  //   delete → hasPermission(pipeline.entity_type_name, 'ahoy_object_deleted')
  // Stage-management (rename/add/move/recolor/delete columns) routes through
  // useUpdatePipelineStages, which PUTs to a pipeline entity_id. Stage-only
  // taxonomies (contact lifecycle) have no pipeline entity_id, so column
  // editing is disabled here — those stages remain editable via Settings →
  // contact lifecycle. Gating on entity_id keeps deal pipelines unchanged.
  const canManageStages = Boolean(selectedPipeline?.entity_id);
  const canEditStage = useMemo(
    () =>
      Boolean(
        canManageStages &&
        selectedPipeline &&
        hasPermission(
          selectedPipeline.entity_type_name,
          'ahoy_property_updated'
        )
      ),
    [canManageStages, selectedPipeline, hasPermission]
  );
  const canDeleteStage = useMemo(
    () =>
      Boolean(
        canManageStages &&
        selectedPipeline &&
        hasPermission(selectedPipeline.entity_type_name, 'ahoy_object_deleted')
      ),
    [canManageStages, selectedPipeline, hasPermission]
  );

  const { mutate: updatePipelineStages, isPending: isUpdatingStages } =
    useUpdatePipelineStages({
      orgId,
      pipelineEntityId: selectedPipeline?.entity_id,
      entityType,
    });

  const handleRenameStage = useCallback(
    (stageId: string, newName: string) => {
      if (!selectedPipeline) {
        return;
      }
      const nextStages = selectedPipeline.stages.map(s => {
        const input = toStageInput(s);
        return s.entity_id === stageId ? { ...input, name: newName } : input;
      });
      updatePipelineStages({
        nextStages,
        errorToast: "Couldn't rename stage. Try again.",
      });
    },
    [selectedPipeline, updatePipelineStages]
  );

  const handleAddStage = useCallback(
    (
      { name, color, probability }: AddStageSubmitData,
      position?: { relativeStageId: string; side: 'left' | 'right' }
    ) => {
      if (!selectedPipeline) {
        return;
      }
      // Sort existing stages by their current order so array indices are
      // the source of truth for the renumbered output.
      const sorted = [...selectedPipeline.stages]
        .sort((a, b) => a.properties.order - b.properties.order)
        .map(toStageInput);
      // Resolve the insertion index. Append at end when no position given,
      // or when the anchor stage no longer exists (defensive fallback).
      const anchorIdx = position
        ? sorted.findIndex(s => s.entity_id === position.relativeStageId)
        : -1;
      const insertIdx =
        anchorIdx < 0
          ? sorted.length
          : anchorIdx + (position?.side === 'right' ? 1 : 0);
      const newStage: PipelineStageInput = {
        name,
        probability,
        order: 0, // renumbered below
        color,
      };
      const nextStages = [
        ...sorted.slice(0, insertIdx),
        newStage,
        ...sorted.slice(insertIdx),
      ].map((s, idx) => ({ ...s, order: idx }));
      updatePipelineStages({
        nextStages,
        errorToast: "Couldn't add stage. Try again.",
      });
    },
    [selectedPipeline, updatePipelineStages]
  );

  const handleMoveStage = useCallback(
    (stageId: string, direction: 'left' | 'right') => {
      if (!selectedPipeline) {
        return;
      }
      const sorted = [...selectedPipeline.stages].sort(
        (a, b) => a.properties.order - b.properties.order
      );
      const idx = sorted.findIndex(s => s.entity_id === stageId);
      const swapWith = direction === 'left' ? idx - 1 : idx + 1;
      if (idx === -1 || swapWith < 0 || swapWith >= sorted.length) {
        return;
      }
      const reordered = [...sorted];
      [reordered[idx], reordered[swapWith]] = [
        reordered[swapWith],
        reordered[idx],
      ];
      const nextStages = reordered.map((s, newIdx) => ({
        ...toStageInput(s),
        order: newIdx,
      }));
      updatePipelineStages({
        nextStages,
        errorToast: "Couldn't move stage. Try again.",
      });
    },
    [selectedPipeline, updatePipelineStages]
  );

  const handleReorderStages = useCallback(
    (orderedStageIds: string[], onRollback?: () => void) => {
      if (!selectedPipeline) {
        return;
      }
      const stageById = new Map(
        selectedPipeline.stages.map(s => [s.entity_id, s] as const)
      );
      const reordered = orderedStageIds
        .map(id => stageById.get(id))
        .filter((s): s is PipelineStageSchema => Boolean(s));
      if (reordered.length !== selectedPipeline.stages.length) {
        return;
      }
      const nextStages = reordered.map((s, newIdx) => ({
        ...toStageInput(s),
        order: newIdx,
      }));
      updatePipelineStages(
        { nextStages, errorToast: "Couldn't reorder stages. Try again." },
        { onError: onRollback }
      );
    },
    [selectedPipeline, updatePipelineStages]
  );

  const handleRecolorStage = useCallback(
    (stageId: string, color: string | null) => {
      if (!selectedPipeline) {
        return;
      }
      const nextStages = selectedPipeline.stages.map(s => {
        const input = toStageInput(s);
        return s.entity_id === stageId ? { ...input, color } : input;
      });
      updatePipelineStages({
        nextStages,
        errorToast: "Couldn't change stage color. Try again.",
      });
    },
    [selectedPipeline, updatePipelineStages]
  );

  const handleDeleteStage = useCallback(
    (stageId: string) => {
      if (!selectedPipeline) {
        return;
      }
      const remaining = selectedPipeline.stages.filter(
        s => s.entity_id !== stageId
      );
      // Renumber order so it's contiguous after the delete.
      const nextStages = [...remaining]
        .sort((a, b) => a.properties.order - b.properties.order)
        .map((s, idx) => ({ ...toStageInput(s), order: idx }));
      updatePipelineStages({
        nextStages,
        errorToast: "Couldn't delete stage. Try again.",
      });
    },
    [selectedPipeline, updatePipelineStages]
  );

  // Convert pipeline stages to PipelineStage format
  const stages = useMemo<PipelineStage[]>(() => {
    if (pipelines.length === 0) {
      return [];
    }
    const pipeline =
      pipelines.find(p => p.entity_id === selectedPipelineId) || pipelines[0];
    if (!pipeline) {
      return [];
    }
    return pipeline.stages
      .filter(s => s.entity_id != null)
      .sort((a, b) => a.properties.order - b.properties.order)
      .map(s => ({
        id: s.entity_id ?? '',
        label: s.label,
        color: s.properties.color,
      }));
  }, [selectedPipelineId, pipelines]);

  // Stage metrics from stage rollup properties
  const stageMetrics = useMemo(() => {
    const result: Record<string, StageMetricsData> = {};
    for (const [id, stage] of stagesByEntityId) {
      if (stage.entity_type_name === 'ahoy_deal_pipeline_stage') {
        // When exchange rates are unavailable, keep per-currency raw totals
        // (labelled by their currency code) rather than showing a zero or
        // unconverted single-currency figure. Never show a converted total
        // that hasn't actually been converted.
        const rawTotalAmounts = (stage.potentialDealTotal ?? []).map(a => ({
          amount: a.amount,
          unit: a.unit,
        }));
        const rawWeightedAmounts = (stage.weightedDealTotal ?? []).map(a => ({
          amount: a.amount,
          unit: a.unit,
        }));
        result[id] = {
          // Deal-stage badge count is computed frontend-side from the loaded (filtered)
          // items the board renders — NOT from the ahoy_deal_count server rollup, which is
          // org-wide (filter-blind) and can drift negative. `undefined` makes the board fall
          // back to the grouped item count (same source as the stage footer).
          count: undefined,
          totalAmount:
            !ratesUnavailable && stage.potentialDealTotalConverted
              ? [stage.potentialDealTotalConverted]
              : rawTotalAmounts,
          weightedAmount:
            !ratesUnavailable && stage.weightedDealTotalConverted
              ? [stage.weightedDealTotalConverted]
              : rawWeightedAmounts,
          properties: stage.properties,
        };
      } else {
        // Non-deal pipeline stages (notes, custom entities) have no server-side
        // count rollup. Some custom entities may expose a numeric `count`
        // property; when absent, leave count `undefined` so the board falls
        // back to the loaded item count instead of forcing 0.
        const count =
          typeof stage.properties?.['count'] === 'number'
            ? stage.properties['count']
            : undefined;
        result[id] = {
          count,
          totalAmount: [],
          weightedAmount: [],
          properties: stage.properties,
        };
      }
    }
    return result;
  }, [stagesByEntityId, ratesUnavailable]);

  // --- Data fetching ---
  // Build properties list for API.
  //
  // Universal system properties plus the config-driven card property names
  // from usePipelineCardConfig — the same config that ConfigurableCardContent
  // renders — so the values are present on the CrmObject when the card renders.
  const queryProperties = useMemo(() => {
    const universal = [
      'ahoy_display_name',
      'ahoy_name',
      'ahoy_updated_at',
      'ahoy_created_at',
    ];
    const result: string[] = [];
    const seen = new Set<string>();
    for (const p of [
      ...universal,
      ...cardPropertyNames,
      ...footerPropertyNames,
    ]) {
      if (seen.has(p)) {
        continue;
      }
      if (UNIVERSAL_PROPERTIES.has(p) || propertySchemas[p] !== undefined) {
        result.push(p);
        seen.add(p);
      }
    }
    return result;
  }, [propertySchemas, cardPropertyNames, footerPropertyNames]);

  // Build include_associations for data fetching
  const includeAssociations = useMemo(() => {
    const avatarPropertiesByType: Record<string, string[]> = {
      ahoy_contact: [
        'ahoy_email',
        'ahoy_profile_photo_url',
        'ahoy_first_name',
        'ahoy_last_name',
        'ahoy_job_title',
        'ahoy_location',
      ],
      ahoy_company: [
        'ahoy_domain',
        'ahoy_industry',
        'ahoy_headcount_range',
        'ahoy_city',
      ],
      ahoy_user: ['ahoy_avatar_url'],
    };

    function propertiesForAssociation(assocTypeName: string): string[] {
      const targetType = associationTypeMap.get(assocTypeName);
      const extra = targetType
        ? (avatarPropertiesByType[targetType] ?? [])
        : [];
      return ['ahoy_display_name', ...extra];
    }

    const associations: Record<string, { properties: string[] }> = {};
    associations[stageAssociationTypeName] = {
      properties: propertiesForAssociation(stageAssociationTypeName),
    };
    if (ownerAssocName && associationTypeMap.has(ownerAssocName)) {
      associations[ownerAssocName] = {
        properties: propertiesForAssociation(ownerAssocName),
      };
    }
    for (const name of cardAssociationNames) {
      if (!associationTypeMap.has(name) || associations[name]) {
        continue;
      }
      associations[name] = { properties: propertiesForAssociation(name) };
    }
    return associations;
  }, [
    associationTypeMap,
    stageAssociationTypeName,
    ownerAssocName,
    cardAssociationNames,
  ]);

  const queryOptions = useMemo(
    () => ({ include_associations: includeAssociations }),
    [includeAssociations]
  );

  const pipelineAssociationTypeName = useMemo(
    () => getPipelineAssociationTypeName(stageAssociationTypeName),
    [stageAssociationTypeName]
  );

  // ---------------------------------------------------------------------------
  // Saved views adapter (filters-only, no view_state, auto-save disabled)
  // ---------------------------------------------------------------------------

  const getFilterConditionGroups = useCallback(
    (): FilterConditionGroup[] =>
      filterConditionGroups.filter(g => g.conditions.length > 0),
    [filterConditionGroups]
  );

  const applyFilterConditionGroups = useCallback(
    (groups: FilterConditionGroup[]) => {
      setFilterConditionGroups(groups);
    },
    []
  );

  const isClauseApplicable = useCallback(
    (clause: CrmFilter) => {
      if (!isAssociationFilter(clause)) {
        return true;
      }
      return (
        clause.association !== stageAssociationTypeName &&
        clause.association !== pipelineAssociationTypeName
      );
    },
    [stageAssociationTypeName, pipelineAssociationTypeName]
  );

  const pipelineSurface = useMemo(
    (): SavedViewSurface => ({
      targetEntityType: entityType,
      getFilterConditionGroups,
      applyFilterConditionGroups,
      isClauseApplicable,
    }),
    [
      entityType,
      getFilterConditionGroups,
      applyFilterConditionGroups,
      isClauseApplicable,
    ]
  );

  // No built-in views on the pipeline surface: useActiveListView(undefined) owns
  // the single ?savedView identity (saved view uuid or null "All"), exactly as
  // useSavedViews did internally before the unification.
  const pipelineActiveView = useActiveListView(undefined, {
    defaultView: defaultViewRef,
  });
  const pipelineSavedViewsOptions = useMemo(
    () => ({ getUrlFilterOverrides }),
    [getUrlFilterOverrides]
  );
  const {
    activeViewId,
    applyView,
    filtersDirtyState,
    viewDirtyState,
    activeSavedViewFilterGroups,
    canWriteActiveView,
    saveViewChanges,
    isSavingViewChanges,
    discardViewChanges,
    selectAll,
    saveModalOpen,
    savePayload,
    openSaveModal,
    closeSaveModal,
    onViewSaved,
    onViewDeleted,
    lastApplyDroppedCount,
    isApplyingView,
    surfaceTransitionPending,
  } = useSavedViews(
    pipelineSurface,
    pipelineActiveView,
    pipelineSavedViewsOptions
  );

  // ---------------------------------------------------------------------------
  // URL sync — the pipeline mirrors its LIVE builder state (it has no
  // applied-snapshot layer); convertConditionGroupsToFilterGroups drops
  // incomplete conditions, so only valid clauses ever reach the URL. The
  // pipeline-scoping clause (pipelineFilterGroups) and `?pipeline=`/`?stage=`
  // params are separate and untouched.
  // ---------------------------------------------------------------------------
  const urlAppliedFilterGroups = useMemo(
    () =>
      convertConditionGroupsToFilterGroups(filterConditionGroups).filter_groups,
    [filterConditionGroups]
  );
  const urlSortDescriptor = useMemo(
    () => ({
      column: sortBy,
      direction:
        sortOrder === 'asc' ? ('ascending' as const) : ('descending' as const),
    }),
    [sortBy, sortOrder]
  );
  const urlApplyFilterGroups = useCallback((groups: FilterGroup[]) => {
    setFilterConditionGroups(filterGroupsToConditionGroups(groups));
  }, []);
  const urlApplySort = useCallback((descriptor: SortDescriptor | null) => {
    if (descriptor) {
      setSortBy(String(descriptor.column));
      setSortOrder(descriptor.direction === 'ascending' ? 'asc' : 'desc');
    } else {
      setSortBy(PIPELINE_DEFAULT_SORT_BY);
      setSortOrder(PIPELINE_DEFAULT_SORT_ORDER);
    }
  }, []);

  useListUrlSync({
    appliedFilterGroups: urlAppliedFilterGroups,
    sortDescriptor: urlSortDescriptor,
    searchText: searchTerm,
    defaultFilterGroups: PIPELINE_EMPTY_DEFAULT_FILTER_GROUPS,
    defaultSortDescriptor: PIPELINE_DEFAULT_SORT_DESCRIPTOR,
    activeSavedViewId: activeViewId,
    filtersDirtyState,
    activeSavedViewFilterGroups,
    // The pipeline never applies a saved view's stored view_state, so the
    // board default is the sort baseline on saved views too.
    activeSavedViewSortDescriptor: PIPELINE_DEFAULT_SORT_DESCRIPTOR,
    surfaceTransitionPending,
    onApplyFilterGroups: urlApplyFilterGroups,
    onApplySort: urlApplySort,
    onApplySearch: setSearchTerm,
  });

  const handleSaveModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeSaveModal();
      }
    },
    [closeSaveModal]
  );

  const handleSetSavedViewAsDefault = useCallback(
    (view: SavedFilterView) =>
      setDefaultView({ kind: 'saved', id: view.entity_id }),
    [setDefaultView]
  );

  const pipelineFilterGroups = useMemo((): FilterGroup[] | undefined => {
    if (!selectedPipelineId) {
      return undefined;
    }
    const filter: AssociationFilter = {
      association: pipelineAssociationTypeName,
      operator: 'exact',
      value: selectedPipelineId,
    };
    return [{ filters: [filter] }];
  }, [selectedPipelineId, pipelineAssociationTypeName]);

  // Build filters
  const filters = useMemo((): CrmObjectFilters => {
    const base: CrmObjectFilters = {
      sort: sortBy,
      order: sortOrder,
      properties: queryProperties,
      limit: 1000,
    };

    if (searchTerm) {
      base.search = searchTerm;
      // ahoy_display_name is universal (every kind defines it); ahoy_name
      // is conditional on the entity actually declaring it (deals do,
      // custom default-kind entities don't).
      base.search_properties = [
        'ahoy_display_name',
        ...(propertySchemas['ahoy_name'] ? ['ahoy_name'] : []),
        ...(propertySchemas['ahoy_amount'] ? ['ahoy_amount'] : []),
      ];
    }

    // Convert filter condition groups to filter_groups format for the backend
    let userFilterGroups: FilterGroup[] | undefined;
    const converted = convertConditionGroupsToFilterGroups(
      filterConditionGroups
    );
    if (converted.filter_groups.length > 0) {
      userFilterGroups = converted.filter_groups;
    }

    // Always apply pipeline scoping filter. If user filters also exist, create
    // a Cartesian product so every resulting group contains BOTH the pipeline
    // constraint AND the user's condition (preserving AND-within-group,
    // OR-between-group semantics). This ensures the pipeline filter can never
    // be silently dropped the way mergeWithDefaultFilters would drop a default
    // group when the user has a filter on the same association name.
    let mergedGroups: FilterGroup[];
    if (
      userFilterGroups &&
      userFilterGroups.length > 0 &&
      pipelineFilterGroups &&
      pipelineFilterGroups.length > 0
    ) {
      mergedGroups = userFilterGroups.flatMap(userGroup =>
        pipelineFilterGroups.map(pipelineGroup => ({
          filters: [...userGroup.filters, ...pipelineGroup.filters],
        }))
      );
    } else if (pipelineFilterGroups && pipelineFilterGroups.length > 0) {
      mergedGroups = pipelineFilterGroups;
    } else if (userFilterGroups && userFilterGroups.length > 0) {
      mergedGroups = userFilterGroups;
    } else {
      mergedGroups = [];
    }
    if (mergedGroups.length > 0) {
      base.filter_groups = mergedGroups;
    }

    return base;
  }, [
    sortBy,
    sortOrder,
    queryProperties,
    searchTerm,
    filterConditionGroups,
    propertySchemas,
    pipelineFilterGroups,
  ]);

  const {
    data: response,
    isLoading,
    refetch,
  } = useCrmObjectList(orgId ?? 0, entityType, filters, queryOptions);

  const objects = useMemo(() => response?.results || [], [response?.results]);

  // While a saved view is being applied (its definition is still being
  // fetched), present the board as loading rather than leaving the previous
  // rows on-screen: columns render skeletons only when empty, so stale cards
  // must be withheld for the loading state to appear. Once the apply commits,
  // the changed query key takes over the loading state seamlessly.
  const boardObjects = useMemo(
    () => (isApplyingView ? [] : objects),
    [isApplyingView, objects]
  );
  const total = response?.count || 0;
  const associatedObjectsRaw = response?.associated_objects;
  const associatedObjects = useMemo(
    () => associatedObjectsRaw ?? {},
    [associatedObjectsRaw]
  );

  // Coordinated refetch
  const handleRefetch = useCallback(async () => {
    await Promise.all([refetch(), refetchStages()]);
  }, [refetch, refetchStages]);

  // --- Create object ---
  const firstStageEntityId = useMemo(() => {
    if (!selectedPipeline || selectedPipeline.stages.length === 0) {
      return undefined;
    }
    const sorted = [...selectedPipeline.stages].sort(
      (a, b) => a.properties.order - b.properties.order
    );
    return sorted[0].entity_id ?? undefined;
  }, [selectedPipeline]);

  // Submit pipeline shared by the toolbar "Add" and the per-column "+":
  // create, then refetch the board in the background.
  const handleCreateSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      if (!currentOrg || !onCreateObject) {
        return;
      }
      const ctx = { orgId: currentOrg.id, entityType };
      try {
        await onCreateObject(currentOrg.id, data);
      } catch (err: unknown) {
        console.error('Failed to create object:', ctx, err);
        throw err;
      }
      await refetch().catch((err: unknown) =>
        console.error('Background list refetch failed after create:', ctx, err)
      );
    },
    [currentOrg, onCreateObject, entityType, refetch]
  );

  // Open the create slideout pre-filled with a stage (or none).
  const openCreateForStage = useCallback(
    (stageId: string | undefined) => {
      if (!currentOrg) {
        return;
      }
      const createTitle = `Create ${title.toLowerCase()}`;
      openCreateSlideout(
        buildGenericCreateConfig({
          orgId: currentOrg.id,
          entityType,
          title: createTitle,
          createProperties,
          createAssociations,
          onSubmit: handleCreateSubmit,
          submitLabel: createTitle,
          initialValues: stageId
            ? { [stageAssociationTypeName]: [stageId] }
            : undefined,
        })
      );
    },
    [
      currentOrg,
      title,
      entityType,
      createProperties,
      createAssociations,
      stageAssociationTypeName,
      handleCreateSubmit,
      openCreateSlideout,
    ]
  );

  const handleOpenCreateModal = useCallback(() => {
    if (!onCreateObject) {
      return;
    }
    openCreateForStage(firstStageEntityId);
  }, [onCreateObject, openCreateForStage, firstStageEntityId]);

  // Handle add to specific stage
  const handleAddToStage = useCallback(
    (stageId: string) => {
      if (!onCreateObject) {
        // No default create pipeline (e.g. an override is registered for
        // this entity type via lib/crm/create/createOverrides.tsx). Forward
        // the column's stageEntityId so the override can pre-select the
        // stage the user clicked from — the same pre-fill the built-in
        // create form gets via `initialValues`.
        onCreateClick?.({ stageEntityId: stageId });
        return;
      }
      openCreateForStage(stageId);
    },
    [onCreateObject, onCreateClick, openCreateForStage]
  );

  // --- Stage change (drag-drop) ---
  const handleStageChange = useDealStageMove({
    orgId,
    userId: user?.id,
    associationTypeName: stageAssociationTypeName,
  });

  // --- View item ---
  const handleViewItem = useCallback(
    (item: CrmObject) => {
      // App-level click (board card): REPLACES any open slideouts.
      openSlideout(item.entity_id);
    },
    [openSlideout]
  );

  // --- Item helpers for board ---
  const getItemId = useCallback((item: CrmObject) => item.id, []);

  const getItemStage = useCallback(
    (item: CrmObject) => {
      const stageAssociation = item.associations?.find(
        (a: AssociationDetail) =>
          a.association_type_name === stageAssociationTypeName
      );
      if (stageAssociation) {
        return stageAssociation.target_entity_id;
      }
      return stages[0]?.id || '';
    },
    [stageAssociationTypeName, stages]
  );

  const setItemStage = useCallback(
    (item: CrmObject, stageEntityId: string): CrmObject => {
      const existingAssociations =
        item.associations?.filter(
          (a: AssociationDetail) =>
            a.association_type_name !== stageAssociationTypeName
        ) || [];

      return {
        ...item,
        associations: [
          ...existingAssociations,
          {
            association_type_name: stageAssociationTypeName,
            target_entity_id: stageEntityId,
          },
        ],
      };
    },
    [stageAssociationTypeName]
  );

  // --- Render card ---
  // (isDealType is hoisted earlier — see comment near propertySchemas.)

  // Schema for the owner association — drives the picker's permission gate.
  const ownerAssociationType = useMemo(
    () =>
      ownerAssocName
        ? associationTypes.find(a => a.association_type_name === ownerAssocName)
        : undefined,
    [associationTypes, ownerAssocName]
  );

  // True when this entity type's display name is composed from separate
  // first/last-name fields — forwarded to InlineCardTitle to force read-only.
  const isComposedName = useMemo(() => isPersonType(entityType), [entityType]);

  const renderCard = useCallback(
    (item: CrmObject) => {
      const ownerAssociation = item.associations?.find(
        (a: AssociationDetail) => a.association_type_name === ownerAssocName
      );
      const owner = ownerAssociation
        ? associatedObjects[ownerAssociation.target_entity_id]
        : undefined;

      return (
        <ConfigurableCardContent
          object={item}
          config={cardConfig}
          propertySchemas={propertySchemas}
          associatedObjects={associatedObjects}
          associationLabels={associationLabels}
          owner={owner}
          ownerAssociationTypeName={ownerAssocName ?? ''}
          ownerAssociationType={ownerAssociationType}
          isComposedName={isComposedName}
        />
      );
    },
    [
      cardConfig,
      ownerAssocName,
      associatedObjects,
      propertySchemas,
      associationLabels,
      ownerAssociationType,
      isComposedName,
    ]
  );

  // --- Sort handling ---
  const currentSortLabel =
    sortOptions.find(o => o.value === sortBy)?.label ?? 'Sort';

  const handleSortSelection = useCallback(
    (key: React.Key) => {
      const selectedKey = String(key);
      if (selectedKey === sortBy) {
        setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortBy(selectedKey);
        setSortOrder('desc');
      }
    },
    [sortBy]
  );

  // --- Filter handling ---
  const handleRemoveFilter = useCallback((conditionId: string) => {
    setFilterConditionGroups(prev =>
      prev.map(g => ({
        ...g,
        conditions: g.conditions.filter(c => c.id !== conditionId),
      }))
    );
  }, []);

  const handleFilterApply = useCallback(() => {
    setShowFilterBuilder(false);
  }, []);

  // --- Property labels for filter chips ---
  const propertyLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [key, schema] of Object.entries(propertySchemas)) {
      labels[key] = schema.label || key;
    }
    return labels;
  }, [propertySchemas]);

  // --- Header info ---
  const pipelineLabel = selectedPipeline
    ? selectedPipeline.label ||
      formatPipelineLabel(selectedPipeline.pipeline_name)
    : pluralTitle;

  // isApplyingView covers the window between clicking a saved-view pill and
  // its filters landing in the query — without it the board shows the old
  // rows with zero feedback while the view definition is fetched.
  // isDefaultResolving suppresses the cold-load flash while a stored default
  // view may still resolve (already gated on deep links by the hook).
  const isDataLoading =
    isLoading ||
    schemaLoading ||
    stagesLoading ||
    pipelinesLoading ||
    isApplyingView ||
    isDefaultResolving;
  const isFiltered = searchTerm.length > 0 || filterConditions.length > 0;

  // --- Header actions ---
  const headerActions = useMemo<HeaderAction[]>(() => {
    const acts: HeaderAction[] = [];
    if (allItemsLabel) {
      acts.push({
        id: 'all-items',
        label: allItemsLabel,
        href: `?${MODE_PARAM}=table`,
        icon: AlignJustify,
        variant: 'unstyled',
      });
    }
    if (currentOrg && isSchemaAdmin) {
      acts.push({
        id: 'manage-pipelines',
        label: 'Manage pipelines',
        href: `/${currentOrg.id}/settings/pipelines`,
        icon: Settings01,
        variant: 'unstyled',
      });
    }
    // Hidden entirely for read-only viewers (no 'ahoy_default' group) — see
    // the residual-affordance pattern in crm-object-list-page.tsx.
    if (canWrite) {
      acts.push({
        id: 'add-deal',
        label: `Add ${title.toLowerCase()}`,
        // Override flows (e.g. the notes composer) receive the SELECTED
        // pipeline's first stage so the page-level button pre-fills the same
        // default the built-in create-form path gets via `initialValues` —
        // creating from a pipeline view defaults to that pipeline's first
        // stage, not the org default's.
        onClick: onCreateClick
          ? () => onCreateClick({ stageEntityId: firstStageEntityId })
          : handleOpenCreateModal,
        variant: 'primary',
      });
    }
    return acts;
  }, [
    allItemsLabel,
    currentOrg,
    isSchemaAdmin,
    canWrite,
    title,
    firstStageEntityId,
    handleOpenCreateModal,
    onCreateClick,
  ]);

  if (!orgId || stages.length === 0) {
    // Still loading, setting up, or no stages configured
    if (isDataLoading) {
      return (
        <div className='flex-1 flex items-center justify-center'>
          <div className='animate-pulse text-[var(--color-text-tertiary)]'>
            Loading pipeline…
          </div>
        </div>
      );
    }
    if (stagesSettingUp) {
      // The stages exist (or are about to) but this browser can't see them
      // yet — a pipeline the org just gained, e.g. right after the notes→tasks
      // migration, while schema and permission caches catch up. The hook keeps
      // polling; say so instead of "not configured", which reads as broken.
      return (
        <div
          className='flex-1 flex items-center justify-center'
          role='status'
          aria-live='polite'
        >
          <div className='text-center'>
            <p className='animate-pulse text-[var(--color-text-tertiary)]'>
              Setting up your pipeline…
            </p>
            <p className='mt-2 text-sm text-[var(--color-text-secondary)]'>
              This usually takes a minute. If it doesn't appear, refresh the
              page.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className='flex-1 flex items-center justify-center'>
        <p className='text-[var(--color-text-secondary)]'>
          No pipeline stages configured.
        </p>
      </div>
    );
  }

  return (
    <CrmListLayout
      entityTitle={pipelineLabel}
      recordCount={total}
      countLabel={pluralTitle}
      countLabelSingular={title}
      contentClassName='bg-bg-primary'
      searchValue={searchTerm}
      onSearch={setSearchTerm}
      searchPlaceholder={`Search ${pluralTitle.toLowerCase()}`}
      hasActiveFilters={filterConditions.length > 0}
      onFilterClick={() => setShowFilterBuilder(!showFilterBuilder)}
      filterConditions={filterConditions}
      propertyLabels={propertyLabels}
      propertySchemas={propertySchemas}
      associationLabels={associationLabelsByName}
      orgId={orgId ?? 0}
      onRemoveFilter={handleRemoveFilter}
      actions={headerActions}
      titleTrailing={
        currentOrg && user ? (
          /*
            `min-w-0 flex-1`: the strip inside SavedViewTabs measures itself to
            decide how many view pills fit before collapsing into "More". That
            measurement needs a width that does NOT depend on its content — a
            content-sized wrapper shrinks with every pill it collapses and
            cascades down to a single pill (PageLayout's title row is flex-1
            for the same reason). The "Partial" badge renders BEFORE the tabs
            so it stays adjacent to the title instead of being pushed to the
            far right by the growing strip.
          */
          <div className='flex min-w-0 flex-1 items-center gap-2'>
            {lastApplyDroppedCount > 0 && activeViewId !== null && (
              <span
                title={`${lastApplyDroppedCount} filter${lastApplyDroppedCount !== 1 ? 's' : ''} in this view aren't supported on the pipeline board`}
                className='inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-800'
              >
                Partial
              </span>
            )}
            <SavedViewTabs
              orgId={currentOrg.id}
              entityType={entityType}
              activeViewId={activeViewId}
              onSelectView={applyView}
              onSelectAll={selectAll}
              onSaveCurrentView={openSaveModal}
              onViewDeleted={onViewDeleted}
              viewDirty={viewDirtyState === 'dirty'}
              canSaveActiveView={canWriteActiveView}
              onSaveActiveViewChanges={saveViewChanges}
              isSavingViewChanges={isSavingViewChanges}
              onDiscardViewChanges={discardViewChanges}
              defaultViewRef={defaultViewRef}
              onSetDefaultView={setDefaultView}
              onClearDefaultView={clearDefaultView}
              canCreateView={canWrite}
            />
          </div>
        ) : undefined
      }
      toolbarExtra={
        <>
          <ToolbarSection>
            {/* Sort dropdown */}
            <Dropdown.Root>
              <Button
                color='tertiary'
                size='sm'
                iconLeading={ArrowDown}
                iconLeadingClassName={cx(
                  '!size-4',
                  sortOrder === 'asc' ? 'rotate-180' : undefined
                )}
                className='!p-0 !gap-1.5 font-semibold text-fg-primary hover:text-fg-secondary hover:!bg-transparent'
              >
                {currentSortLabel}
              </Button>
              <Dropdown.Popover>
                <Dropdown.Menu
                  onAction={handleSortSelection}
                  selectedKeys={new Set([sortBy])}
                >
                  {sortOptions.map(opt => {
                    let addon: string | undefined;
                    if (sortBy === opt.value) {
                      addon = sortOrder === 'asc' ? '↑' : '↓';
                    }
                    return (
                      <Dropdown.Item
                        key={opt.value}
                        id={opt.value}
                        textValue={opt.label}
                        label={opt.label}
                        addon={addon}
                      />
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </ToolbarSection>

          {/* Spacer for future toolbar items */}
          <div className='flex-1' />
        </>
      }
    >
      {/* Filter slideout */}
      {currentOrg && (
        <SlideoutMenu
          isOpen={showFilterBuilder}
          onOpenChange={setShowFilterBuilder}
        >
          <SlideoutMenu.Header onClose={() => setShowFilterBuilder(false)}>
            <h2 className='text-lg font-semibold text-primary'>
              Filter {pluralTitle}
            </h2>
          </SlideoutMenu.Header>
          <FormProvider fields={filterFieldConfig} values={filterFormValues}>
            <SlideoutMenu.Content>
              <SchemaFilterBuilder
                orgId={currentOrg.id}
                entityType={entityType}
                groups={builderGroups}
                onChange={handleGroupsChange}
                allowGroups={true}
                onApply={handleFilterApply}
                onValidityChange={setFiltersAreValid}
                enableAssociatedPropertyFilters
              />
            </SlideoutMenu.Content>
            <SlideoutMenu.Footer className='flex w-full items-center justify-between gap-3'>
              <Button
                size='md'
                color='tertiary'
                onClick={() => {
                  setFilterConditionGroups([]);
                  setShowFilterBuilder(false);
                }}
              >
                Reset filters
              </Button>
              <SubmitButton
                size='md'
                color='primary'
                onClick={() => {
                  handleFilterApply();
                }}
              >
                Apply filters
              </SubmitButton>
            </SlideoutMenu.Footer>
          </FormProvider>
        </SlideoutMenu>
      )}

      {/* Pipeline error banner */}
      {pipelinesError && !isTransientError(pipelinesError) && (
        <div className='px-4 py-2 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400'>
          We couldn't load pipelines. Refresh the page and try again.
        </div>
      )}

      <div className='flex-1 min-h-0 flex flex-col overflow-hidden pt-4 border-t border-[var(--color-border-secondary)]'>
        <PipelineBoard
          items={boardObjects}
          stages={stages}
          getItemId={getItemId}
          getItemStage={getItemStage}
          setItemStage={setItemStage}
          renderCard={renderCard}
          onStageChange={handleStageChange}
          onItemClick={handleViewItem}
          onRefresh={handleRefetch}
          stageMetrics={stageMetrics}
          // Per-column "+" creates a record in that stage — the same write the
          // page-level "Add …" button guards, so it follows the same seat gate.
          onAddToStage={canWrite ? handleAddToStage : undefined}
          isFiltered={isFiltered}
          loading={isDataLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          sortLabel={sortLabel}
          openItemId={slideoutTopId ?? null}
          onRenameStage={handleRenameStage}
          onAddStage={handleAddStage}
          onMoveStage={handleMoveStage}
          onReorderStages={handleReorderStages}
          onRecolorStage={handleRecolorStage}
          showProbability={isDealType}
          initialScrollToStageId={searchParams.get('stage')}
          isUpdating={isUpdatingStages}
          onDeleteStage={handleDeleteStage}
          canEditStage={canEditStage}
          canDeleteStage={canDeleteStage}
          footerCalculations={footerConfig.calculations}
          footerPropertySchemas={propertySchemas}
        />
      </div>

      {/* Save current view modal (filters-only) */}
      {currentOrg && savePayload && (
        <SaveCurrentViewModal
          isOpen={saveModalOpen}
          onOpenChange={handleSaveModalOpenChange}
          orgId={currentOrg.id}
          entityType={entityType}
          payload={savePayload}
          onSaved={onViewSaved}
          orgName={currentOrg.name}
          onSetAsDefault={handleSetSavedViewAsDefault}
        />
      )}
    </CrmListLayout>
  );
}
